import {
    toDocumentImportFailureMessage,
    type JobProgressMessage,
} from '@quipex/core/realtime';
import { activityIdentityForJob } from './identity';
import { parseActivityMessage } from './message';
import type { ActivityJobUpdate, ActivityKey, ActivityStage } from './models';

export function isActivityJobProgressMessage(
    message: JobProgressMessage
): boolean {
    return mapActivityJob(message) !== null;
}

export function toActivityKey(message: JobProgressMessage): ActivityKey {
    const correlationId = normalizeIdentifier(message.correlationId);
    const jobId = message.jobId.trim();
    const jobType = message.jobType.trim();
    return activityIdentityForJob(
        jobType,
        jobId,
        correlationId?.toLowerCase() ?? null
    ).key;
}

export function toActivityStage(jobType: string): ActivityStage {
    return activityIdentityForJob(jobType, '', null).stage;
}

export function mapActivityJob(
    message: JobProgressMessage
): ActivityJobUpdate | null {
    const jobId = message.jobId.trim();
    const jobType = message.jobType.trim();
    const occurredAtUtc = normalizeTimestamp(message.occurredAtUtc);
    const correlationId = normalizeIdentifier(message.correlationId);
    const identity = activityIdentityForJob(
        jobType,
        jobId,
        correlationId?.toLowerCase() ?? null
    );

    if (!jobId || !jobType || !occurredAtUtc || !identity.supported) {
        return null;
    }

    const importFailureMessage =
        jobType === 'DocumentImport'
            ? toDocumentImportFailureMessage({
                  code: message.failureCode,
                  actualBytes: message.actualBytes,
                  allowedBytes: message.allowedBytes,
              })
            : null;
    const itemCounts = normalizeItemCounts(
        message.completedItems,
        message.totalItems
    );
    const exportIdentity = exportIdentityFor(message);

    return {
        key: identity.key,
        correlationId: correlationId?.toLowerCase() ?? null,
        jobId,
        jobType,
        stage: identity.stage,
        status: message.status,
        warning:
            message.status === 'Completed' && importFailureMessage !== null,
        percentComplete: normalizePercent(message.percentComplete),
        completedItems: itemCounts.completed,
        totalItems: itemCounts.total,
        message:
            importFailureMessage ??
            parseActivityMessage(message.message) ??
            null,
        occurredAtUtc,
        documentPublicId: normalizeIdentifier(message.documentPublicId),
        buildingPublicId: normalizeIdentifier(message.buildingId),
        packId: exportIdentity.packId,
        exportKind: exportIdentity.kind,
        buildingName: null,
    };
}

function exportIdentityFor(message: JobProgressMessage): {
    readonly kind: 'documents' | 'selected-documents' | 'pack' | null;
    readonly packId: string | null;
} {
    if (message.jobType.trim() === 'SelectedBuildingDocumentExport')
        return { kind: 'selected-documents', packId: null };
    if (message.jobType.trim() === 'BuildingDocumentExport') {
        return { kind: 'documents', packId: null };
    }

    if (message.jobType.trim() === 'DocumentPackExport') {
        return { kind: 'pack', packId: normalizeIdentifier(message.packId) };
    }

    return { kind: null, packId: null };
}

function normalizeItemCounts(
    completedItems: number | null | undefined,
    totalItems: number | null | undefined
): { readonly completed: number | null; readonly total: number | null } {
    const completed = normalizeItemCount(completedItems);
    const total = normalizeItemCount(totalItems);

    return completed !== null && total !== null && completed <= total
        ? { completed, total }
        : { completed: null, total: null };
}

function normalizeItemCount(value: number | null | undefined): number | null {
    return typeof value === 'number' && Number.isInteger(value) && value >= 0
        ? value
        : null;
}

function normalizeIdentifier(value: string | null | undefined): string | null {
    const normalized = value?.trim();
    return normalized || null;
}

function normalizeTimestamp(value: string): string | null {
    const normalized = value.trim();
    return normalized && Number.isFinite(Date.parse(normalized))
        ? normalized
        : null;
}

function normalizePercent(value: number | null): number | null {
    return value !== null &&
        Number.isFinite(value) &&
        value >= 0 &&
        value <= 100
        ? value
        : null;
}
